import i18next from "../i18n";

type EnvKey =
  | "local-aws-wbai"
  | "local-aws-lseg"
  | "local-az-wbai"
  | "local-az-ngv"
  | "local-az-nget"
  | "dev-aws-wbai"
  | "dev-aws-lseg"
  | "dev-az-wbai"
  | "dev-az-ngv"
  | "dev-az-nget"
  | "prod-aws-wbai"
  | "prod-az-wbai"
  | "prod-az-ngv"
  | "prod-az-nget"
  | "production"; // Keep for backwards compatibility

interface APIConfig {
  admin: string;
  platform: string;
  data: string;
}

/**
 * Components of a Vite mode string (`<env>-<cloud>-<tenant>`), typed as unions
 * so the lookup tables and checks in `getAPIURL` are compile-checked. These mode
 * tokens are a separate vocabulary from the react-ui tenant keys in
 * `config/tenants.ts` — e.g. the mode token is `wbai`, whereas the pack key is
 * `webuild-ai.com`. A typo such as an `"ngvv"` table key is therefore a type
 * error rather than an entry that silently never matches.
 */
type ModeEnv = "local" | "dev" | "prod" | "production";
type ModeCloud = "aws" | "az";
type ModeTenant = "wbai" | "ngv" | "nget" | "lseg";

/** Local modes always reach the three services on localhost. */
const localAPIConfig = (): APIConfig => ({
  admin: "http://localhost:8000",
  platform: "http://localhost:8001",
  data: "http://localhost:8002",
});

/** Outside local development every service sits behind one base URL. */
const sharedBaseAPIConfig = (baseUrl: string): APIConfig => ({
  admin: baseUrl,
  platform: baseUrl,
  data: baseUrl,
});

/** The NGV dev backend, which NGET dev also uses until its own is provisioned. */
const NGV_DEV_API_BASE = "https://api.pathway.ngvdev.az.webuild-ai.com";

/**
 * Backends for the hostnames the "production" mode recognises. A `Map` rather
 * than an object literal, so a hostname such as `constructor` cannot match an
 * `Object.prototype` member and is given the default pattern instead.
 */
const PRODUCTION_HOST_API_BASES = new Map<string, string>([
  // NGV Dev (nationalgrid.com)
  ["dev-ai.ngv.nationalgrid.com", "https://cetcrm-dev-ai.ngv.nationalgrid.com"],
  ["ngvdveus2stfrontend.z20.web.core.windows.net", "https://cetcrm-dev-ai.ngv.nationalgrid.com"],
  // NGV UAT (nationalgrid.com)
  ["uat-ai.ngv.nationalgrid.com", "https://cetcrm-uat-ai.ngv.nationalgrid.com"],
  // NGV Production (nationalgrid.com)
  ["ai.ngv.nationalgrid.com", "https://cetcrm.ngv.nationalgrid.com"],
  // NGV Dev Domain (webuild-ai.com)
  ["pathway.ngvdev.az.webuild-ai.com", NGV_DEV_API_BASE],
  // NGET Dev (nationalgrid.com)
  ["dev-ai.nget.nationalgrid.com", "https://api-dev-ai.nget.nationalgrid.com"],
  // NGET UAT (nationalgrid.com)
  ["uat-ai.nget.nationalgrid.com", "https://api-uat-ai.nget.nationalgrid.com"],
  // NGET Production (nationalgrid.com)
  ["ai.nget.nationalgrid.com", "https://api-ai.nget.nationalgrid.com"],
  // NGET Dev Domain (webuild-ai.com)
  // TODO(NGET): swap to NGET-specific hostname once the NGET dev backend is provisioned.
  ["pathway.ngetdev.az.webuild-ai.com", NGV_DEV_API_BASE],
]);

/** A recognised production hostname's backend, else the default `api.<hostname>` pattern. */
function productionHostAPIBase(hostname: string): string {
  return PRODUCTION_HOST_API_BASES.get(hostname) ?? `https://api.${hostname}`;
}

/**
 * The backends of one deployed environment: those of the tenants that have
 * their own, then the shared webuild-ai.com backend for each cloud. Any cloud
 * other than `az` takes the AWS one.
 */
interface DeployedAPIBases {
  tenants: Map<ModeTenant | undefined, string>;
  az: string;
  aws: string;
}

/** Deployed backends by environment. Any other environment has none, so resolves to "". */
const DEPLOYED_API_BASES = new Map<ModeEnv, DeployedAPIBases>([
  [
    "dev",
    {
      tenants: new Map<ModeTenant | undefined, string>([
        ["ngv", NGV_DEV_API_BASE],
        // Temporary alias: NGET dev shares the NGV dev backend until the NGET dev API
        // is provisioned. Restore "https://api-dev-ai.nget.nationalgrid.com" once ready.
        ["nget", NGV_DEV_API_BASE],
        // LSEG dev runs on the AWS risk environment (infrastructure repo,
        // infra/aws/envs/risk-dev): frontend at risk.dev.webuild-ai.com,
        // APIs behind api.risk.dev.webuild-ai.com.
        ["lseg", "https://api.risk.dev.webuild-ai.com"],
      ]),
      az: "https://api.pathway.dev.az.webuild-ai.com",
      aws: "https://api.pathway.dev.webuild-ai.com",
    },
  ],
  [
    "prod",
    {
      tenants: new Map<ModeTenant | undefined, string>([
        ["ngv", "https://cetcrm.ngv.nationalgrid.com"],
        ["nget", "https://api-ai.nget.nationalgrid.com"],
      ]),
      az: "https://api.pathway.az.webuild-ai.com",
      aws: "https://api.pathway.webuild-ai.com",
    },
  ],
]);

/** The base URL a deployed (`dev-*` or `prod-*`) mode resolves to. */
function deployedAPIBase(env: ModeEnv, cloud?: ModeCloud, tenant?: ModeTenant): string {
  const bases = DEPLOYED_API_BASES.get(env);

  if (!bases) return "";

  return bases.tenants.get(tenant) ?? (cloud === "az" ? bases.az : bases.aws);
}

function getAPIURL(mode: EnvKey): APIConfig {
  // Parse mode into components. The cast reflects the fixed `EnvKey` grammar;
  // the "production" mode has no cloud/tenant part, so both may be undefined.
  const [env, cloud, tenant] = mode.split("-") as [ModeEnv, ModeCloud?, ModeTenant?];

  // Local environments always use localhost
  if (env === "local") return localAPIConfig();

  // Production mode - detect from hostname
  if (mode === "production") {
    return sharedBaseAPIConfig(productionHostAPIBase(window.location.hostname));
  }

  // Build API URLs based on pattern
  return sharedBaseAPIConfig(deployedAPIBase(env, cloud, tenant));
}

export const siteConfig = () => {
  const mode = import.meta.env.MODE as EnvKey;
  const apiURLs = getAPIURL(mode);

  return {
    name: i18next.t("vite-heroui"),
    description: i18next.t("make-beautiful-websites-regardless-of-your-design-experience"),
    navItems: [
      {
        label: i18next.t("home"),
        href: "/",
      },
      {
        label: i18next.t("chat"),
        href: "/workflows/chat_agent",
      },
      {
        label: i18next.t("workflows"),
        href: "/workflows",
      },
      {
        label: i18next.t("knowledge"),
        href: "/knowledge",
      },
    ],
    navMenuItems: [
      {
        label: i18next.t("profile"),
        href: "/profile",
      },
      {
        label: i18next.t("dashboard"),
        href: "/dashboard",
      },
      {
        label: i18next.t("projects"),
        href: "/projects",
      },
      {
        label: i18next.t("team"),
        href: "/team",
      },
      {
        label: i18next.t("calendar"),
        href: "/calendar",
      },
      {
        label: i18next.t("settings"),
        href: "/settings",
      },
      {
        label: i18next.t("help-and-feedback"),
        href: "/help-feedback",
      },
      {
        label: i18next.t("logout"),
        href: "/logout",
      },
    ],
    links: {
      github: "https://github.com/webuild-ai/pathway",
      twitter: "https://twitter.com/webuild_ai",
      docs: "https://webuild-ai.com",
    },
    environment: mode,
    adminAPIURL: apiURLs.admin,
    platformAPIURL: apiURLs.platform,
    dataHubAPIURL: apiURLs.data,
    aws_rum_application_id: (import.meta.env.VITE_AWS_RUM_APPLICATION_ID as string) || "",
    aws_rum_region: (import.meta.env.VITE_AWS_RUM_REGION as string) || "us-east-1",
    aws_rum_identity_pool_id: (import.meta.env.VITE_AWS_RUM_IDENTITY_POOL_ID as string) || "",
    aws_rum_guest_role_arn: (import.meta.env.VITE_AWS_RUM_GUEST_ROLE_ARN as string) || "",
    features: {},
  };
};

export type SiteConfig = ReturnType<typeof siteConfig>;
